import { ButtonHTMLAttributes, forwardRef, useCallback } from 'react'

import { useParticipantsToggle } from '@/src/hooks'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

/** @public */

export interface ParticipantsButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  toggleParticipant?: boolean
  visibleControls?: boolean
  closeBar?: boolean
}

/**
 * The `DisconnectButton` is a basic html button with the added ability to disconnect from a LiveKit room.
 * Normally this is the big red button that allows end users to leave the video or audio call.
 *
 * @example
 * ```tsx
 * <LiveKitRoom>
 *   <DisconnectButton>Leave room</DisconnectButton>
 * </LiveKitRoom>
 * ```
 * @public
 */

export const ParticipantsButton = forwardRef<HTMLButtonElement, ParticipantsButtonProps>(
  function ParticipantsButton({ closeBar, ...otherProps }: ParticipantsButtonProps, ref) {
    const { mergedProps, waitParticipants, showButton } = useParticipantsToggle({
      props: otherProps,
    })

    const { primary_color } = useAppSelector(selectBranding)

    const getCountOfWaitingParticipants = useCallback(
      () => (waitParticipants ? (waitParticipants < 10 ? waitParticipants.toFixed(0) : '9+') : '0'),
      [waitParticipants],
    )

    return (
      showButton && (
        <div style={{ position: closeBar ? 'static' : 'relative', display: 'flex' }}>
          {waitParticipants !== 0 && !closeBar && (
            <span className="lk-counter" style={{ background: `${primary_color}` }}>
              {getCountOfWaitingParticipants()}
            </span>
          )}

          <button ref={ref} {...mergedProps}>
            {otherProps.children}
          </button>
        </div>
      )
    )
  },
)
